import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import api from '../../services/api';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Video,
  Ticket,
  RefreshCw,
  Loader2,
  Calendar,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const AdminAnalytics = () => {
  const [dashboard, setDashboard] = useState(null);
  const [revenueChart, setRevenueChart] = useState(null);
  const [webinarPerformance, setWebinarPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState('30d');

  useEffect(() => {
    fetchData();
  }, [chartPeriod]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, revenueRes, webinarRes] = await Promise.all([
        api.get('/analytics/dashboard'),
        api.get(`/analytics/revenue/chart?period=${chartPeriod}`),
        api.get('/analytics/webinars/performance')
      ]);
      setDashboard(dashboardRes.data);
      setRevenueChart(revenueRes.data);
      setWebinarPerformance(webinarRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const GrowthIndicator = ({ value }) => {
    if (value === 0) return <span className="text-gray-500">0%</span>;
    return value > 0 ? (
      <span className="text-green-600 flex items-center text-sm">
        <ArrowUpRight className="h-4 w-4" />
        {value}%
      </span>
    ) : (
      <span className="text-red-600 flex items-center text-sm">
        <ArrowDownRight className="h-4 w-4" />
        {Math.abs(value)}%
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-analytics-title">
            Analytics & Reportes
          </h1>
          <p className="text-gray-500 mt-1">
            Métricas de rendimiento de la plataforma
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Revenue */}
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Ingresos del Mes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(dashboard?.revenue?.this_month || 0)}
                </p>
                <div className="flex items-center mt-1">
                  <GrowthIndicator value={dashboard?.revenue?.growth_percent || 0} />
                  <span className="text-xs text-gray-500 ml-2">vs mes anterior</span>
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-green-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Members */}
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Miembros Activos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {dashboard?.members?.active || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  +{dashboard?.members?.new_this_month || 0} este mes
                </p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Webinars */}
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Webinars</p>
                <p className="text-2xl font-bold text-gray-900">
                  {dashboard?.webinars?.total || 0}
                </p>
                <div className="flex gap-2 mt-1">
                  <Badge className="bg-green-100 text-green-700 text-xs">
                    {dashboard?.webinars?.free || 0} gratis
                  </Badge>
                  <Badge className="bg-amber-100 text-amber-700 text-xs">
                    {dashboard?.webinars?.paid || 0} pago
                  </Badge>
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Video className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Coupons */}
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Cupones Usados</p>
                <p className="text-2xl font-bold text-gray-900">
                  {dashboard?.coupons?.total_uses || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {dashboard?.coupons?.active_coupons || 0} cupones activos
                </p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Ticket className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Ingresos</CardTitle>
            <Select value={chartPeriod} onValueChange={setChartPeriod}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">7 días</SelectItem>
                <SelectItem value="30d">30 días</SelectItem>
                <SelectItem value="90d">90 días</SelectItem>
                <SelectItem value="365d">1 año</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              {revenueChart?.data?.length > 0 ? (
                <div className="w-full">
                  <div className="text-center mb-4">
                    <p className="text-3xl font-bold text-gray-900">
                      {formatCurrency(revenueChart.total)}
                    </p>
                    <p className="text-sm text-gray-500">Total en {chartPeriod}</p>
                  </div>
                  {/* Simple bar representation */}
                  <div className="flex items-end justify-between h-32 gap-1">
                    {revenueChart.data.slice(-14).map((d, i) => {
                      const maxValue = Math.max(...revenueChart.data.map(x => x.revenue)) || 1;
                      const height = (d.revenue / maxValue) * 100;
                      return (
                        <div
                          key={i}
                          className="flex-1 bg-green-500 rounded-t hover:bg-green-600 transition-colors"
                          style={{ height: `${Math.max(height, 2)}%` }}
                          title={`${d.date}: ${formatCurrency(d.revenue)}`}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Sin datos de ingresos</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Webinar Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Rendimiento de Webinars</CardTitle>
          </CardHeader>
          <CardContent>
            {webinarPerformance?.webinars?.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {webinarPerformance.webinars.slice(0, 5).map((webinar) => (
                  <div key={webinar.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{webinar.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {webinar.registrations} registros
                        </Badge>
                        {!webinar.is_free && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            {formatCurrency(webinar.revenue)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <Video className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Sin datos de webinars</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-green-500 flex items-center justify-center">
                <DollarSign className="h-7 w-7 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-700">Ingresos Totales</p>
                <p className="text-3xl font-bold text-green-900">
                  {formatCurrency(dashboard?.revenue?.total || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-blue-500 flex items-center justify-center">
                <Users className="h-7 w-7 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-700">Total Miembros</p>
                <p className="text-3xl font-bold text-blue-900">
                  {dashboard?.members?.total || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-purple-500 flex items-center justify-center">
                <Video className="h-7 w-7 text-white" />
              </div>
              <div>
                <p className="text-sm text-purple-700">Total Registros</p>
                <p className="text-3xl font-bold text-purple-900">
                  {dashboard?.webinars?.total_registrations || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500">
        Última actualización: {dashboard?.generated_at ? new Date(dashboard.generated_at).toLocaleString('es-MX') : '-'}
      </div>
    </div>
  );
};

export default AdminAnalytics;
